import React from 'react';
import { Video, ExternalLink, X } from 'lucide-react';

const VideoViewer = ({ 
  isOpen, 
  onClose, 
  resource, 
  title 
}) => {
  if (!isOpen || !resource) return null;

  const { resource_url, resource_name } = resource;

  // YouTube detection functions
  const getYouTubeVideoId = (url) => {
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[7].length === 11) ? match[7] : null;
  };

  const isYouTubeUrl = (url) => {
    return url && (url.includes('youtube.com') || url.includes('youtu.be'));
  };

  const isYouTubeResource = isYouTubeUrl(resource_url) && getYouTubeVideoId(resource_url);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className={`bg-white max-w-4xl max-h-[90vh] w-full overflow-hidden relative rounded-lg`}>
        {/* Close button - positioned differently for YouTube vs other content */}
        <button
          onClick={onClose}
          className={`${isYouTubeResource 
            ? 'absolute top-4 right-4 z-20 bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full p-2' 
            : 'absolute top-4 right-4 z-10 text-gray-500 hover:text-gray-700'
          }`}
        >
          <X className="w-6 h-6" />
        </button>

        {/* Header - only show for non-YouTube content */}
        {!isYouTubeResource && (
          <div className="flex justify-between items-center p-4 border-b pr-16">
            <h3 className="text-xl font-semibold text-gray-900">{title || resource_name}</h3>
          </div>
        )}
        
        <div className={`${isYouTubeResource ? '' : 'p-4'}`}>
          {isYouTubeResource ? (
            <div className="aspect-video">
              <iframe
                width="100%"
                height="100%"
                src={`https://www.youtube.com/embed/${getYouTubeVideoId(resource_url)}?controls=1&showinfo=1&rel=0`}
                title={title || resource_name}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="rounded-lg"
              ></iframe>
            </div>
          ) : resource_url?.match(/\.(mp4|webm|ogg|mov|avi)$/i) ? (
            <div className="space-y-4 p-4">
              <div className="text-center py-4">
                <Video className="w-16 h-16 text-blue-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">Video Player</p>
              </div>
              <video controls className="w-full rounded-lg">
                <source src={resource_url} />
                Your browser does not support the video element.
              </video>
            </div>
          ) : (
            <div className="text-center py-8 p-4">
              <Video className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">Video player not available for this URL format</p>
              <a
                href={resource_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open External Link
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoViewer;